﻿<template>
	<common-page
		:label="ts( 'openFromFile' )" :ok-btn="stageBtn" :cancel-btn="preview ? ts( 'pickAnotherFile' ) : ''"
		:use-second-button="preview"
		@ok="proceed" @cancel="pickAnother">

		<div v-if="preview">
			<q-markup-table flat>
				<tbody>
				<tr>
					<td class="text-left text-weight-bold">{{ ts( 'appVersion' ) }}</td>
					<td>{{ fileVersion }}</td>
				</tr>
				<tr>
					<td class="text-left text-weight-bold">{{ ts( 'fileTimestamp' ) }}</td>
					<td>{{ fileTimestamp }}</td>
				</tr>
				<tr>
					<td class="text-left text-weight-bold">{{ ts( 'csjTimestamp' ) }}</td>
					<td>{{ ts( 'whoAndWhen', { who: csjScorer, when: csjTimestamp } ) }}</td>
				</tr>
				<tr>
					<td class="text-left text-weight-bold">{{ ts( 'event' ) }}</td>
					<td>{{ eventDesc }}</td>
				</tr>
				<tr>
					<td class="text-left text-weight-bold vertical-top">{{ ts( 'boatClasses' ) }}</td>
					<td>
						<q-markup-table flat>
							<tbody>
							<tr v-for="( eventClass ) in eventClasses">
								<td>{{ eventClass.classDesc }}</td>
								<td class="text-right">{{ tcs( 'boats', eventClass.boats ) }}</td>
								<td class="text-right">{{ tcs( 'races', eventClass.races ) }}</td>
							</tr>
							</tbody>
						</q-markup-table>
					</td>
				</tr>
				</tbody>
			</q-markup-table>

			<div class="text-negative q-mt-lg q-mb-lg" v-if="duplicateEvent">
				<q-icon name="error" size="2em" color="negative"/>
				<span class="q-ml-sm text-weight-bold">{{ ts( 'duplicateEvent' ) }}</span>
			</div>
		</div>
		<q-file
			v-else
			v-model="uploadedFile" autofocus flat :label="ts( 'pickFile' )"
			accept="application/json" clearable counter :counter-label="formatCounter">
			<template v-slot:prepend>
				<q-icon name="drive_folder_upload"/>
			</template>
		</q-file>
	</common-page>
</template>

<script setup lang="ts">
import CommonPage from '@/components/parts/commonPage.vue'
import { createEventFromFile } from '@/network/importer'
import { DateFormat, ds, tcs, ts } from '@/plugins/i18nFormat'
import EventsRepo from '@repo/EventsRepo'
import destr from "destr"
import { useRepo } from 'pinia-orm'
import { Loading, useQuasar } from 'quasar'
import { computed, ref, nextTick } from 'vue'
import { useRouter } from 'vue-router'

const quasar = useQuasar()
const router = useRouter()

const uploadedFile = ref()

const preview = ref( false )
const duplicateEvent = ref( false )
const fileVersion = ref( '' )
const fileTimestamp = ref( '' )
const csjTimestamp = ref( '' )
const csjScorer = ref( '' )
const eventDesc = ref( '' )
const eventClasses = ref( [] )

const stageBtn = computed( () => ts( preview.value ? 'importEvent' : 'importFile' ) )

const importedContent = ref( {} )

function formatCounter ( { totalSize } ) {
	return String( totalSize ).replace( /^(\d+(?:\.\d+)?)(.*)$/, '$1 $2' )
}

function pickAnother () {
	preview.value = false
	uploadedFile.value = null
	duplicateEvent.value = false
}

function proceed () {
	if ( preview.value )
		doImport()
	else
		doPreview()
}

async function doImport () {
	Loading.show( {
		message: ts( 'importingEvent' )
	} )

	await nextTick()
	await new Promise( resolve => setTimeout( resolve, 100 ) )

	const eventId = await createEventFromFile( importedContent.value )

	Loading.hide()

	preview.value = false
	uploadedFile.value = null
	duplicateEvent.value = false

	importedContent.value = {}

	if ( eventId !== undefined )
		router.push( { name: 'EventManager', params: { eventId: eventId } } )
}

function fillPreview ( content ) {
    preview.value = true

    const eventFrom = content.event.from.toDate()
    const eventTo = content.event.to === null ? null : content.event.to.toDate()

    fileVersion.value = content.version
    fileTimestamp.value = ds( content.generated.toDate(), DateFormat.DateTimeHuman )
    csjTimestamp.value = ds( content.sailingDataTimestamp.toDate(), DateFormat.DateTimeHuman )
    csjScorer.value = content.authorizedScorer
    eventDesc.value = content.event.ctl + ' ' + content.event.title
        + ' (' + ds( eventFrom, ( eventTo != null && !eventFrom.isSameAs( eventTo ) ) ? DateFormat.EventFrom : DateFormat.EventTo )
        + ( ( eventTo == null || eventFrom.isSameAs( eventTo ) ) ? '' : ' ' + ts( 'till' ) + ' ' + ds( eventTo, DateFormat.EventTo ) ) + ')'

    eventClasses.value = []

    const boats = content.event.boats.reduce( ( acc, cur ) => acc.set( cur.id, cur.boatClassId ), new Map<string, string>( [] ) )

    content.event.classes.forEach( function ( cl ) {
        eventClasses.value.push( {
            classDesc: cl.shortcut,
            weight: cl.weight,
            shortcut: cl.shortcut,
            boats: content.event.boats.filter( b => b.boatClassId === cl.boatClassId ).length,
            races: content.event.races.filter( r => r.results.filter( res => boats.get( res.eventBoatId ) === cl.boatClassId ).length > 0 ).length,
        } )
    } )

    eventClasses.value = eventClasses.value.sort( ( a, b ) => a.weight === b.weight ? a.shortcut.localeCompare( b.shortcut ) : b.weight - a.weight )

    duplicateEvent.value = useRepo( EventsRepo ).eventByCtlId( content.event.ctl ) != null
}

function doPreview () {
	if ( uploadedFile.value === undefined ) {
		quasar.notify( {
			icon: 'error',
			color: 'negative',
			message: ts( 'pleaseChooseFile' ),
			timeout: 5000,
		} )

		return
	}

	const file = uploadedFile.value
	const reader = new FileReader()

	reader.onload = () => {
		importedContent.value = {}

		try {
			const rawContent = String( reader.result )
			importedContent.value = destr( rawContent )
		}
		catch ( e ) {
			quasar.notify( {
				icon: 'error',
				color: 'negative',
				message: ts( 'invalidFileFormat', { fileName: uploadedFile.value.fileName } ),
				timeout: 5000,
			} )

			return
		}

        if ( typeof importedContent.value !== 'object' || !Object.hasOwn( importedContent.value, 'software' ) || importedContent.value.software !== 'Score System' || !Object.hasOwn( importedContent.value, 'version' ) ) {
            quasar.notify( {
                icon: 'error',
                color: 'negative',
                message: ts( 'notMySaveFile', { fileName: uploadedFile.value.fileName } ),
                timeout: 5000,
            } )

            return
        }

        fillPreview( importedContent.value )
	}

	reader.readAsText( file )
}
</script>
